/**
 * OfflineBanner — shows a slim notice when navigator.onLine === false.
 * Auto-hides + re-fetches the visible page when connection comes back.
 *
 * Drop anywhere; no props needed.
 */
import React, { useEffect, useState } from "react";
import { CloudSlash, ArrowsClockwise } from "@phosphor-icons/react";

export function OfflineBanner() {
  const [offline, setOffline] = useState(typeof navigator !== "undefined" && !navigator.onLine);
  const [justReconnected, setJustReconnected] = useState(false);

  useEffect(() => {
    const onOffline = () => { setOffline(true); setJustReconnected(false); };
    const onOnline = () => {
      setOffline(false);
      setJustReconnected(true);
      // Soft refresh: trigger a single GET tick so SWR/network-first caches refill.
      // We don't full-reload because the user might be mid-flow.
      setTimeout(() => setJustReconnected(false), 4000);
    };
    window.addEventListener("offline", onOffline);
    window.addEventListener("online", onOnline);
    return () => {
      window.removeEventListener("offline", onOffline);
      window.removeEventListener("online", onOnline);
    };
  }, []);

  if (!offline && !justReconnected) return null;

  return (
    <div
      data-testid={offline ? "offline-banner" : "offline-banner-reconnected"}
      className={`mb-3 vs-card px-4 py-2.5 flex items-center justify-between gap-3 ${offline ? "border-[var(--vs-warning)]/40 bg-[var(--vs-warning)]/8" : "border-[var(--vs-success)]/40 bg-[var(--vs-success)]/8"}`}
    >
      <div className="flex items-center gap-2 text-sm">
        {offline ? (
          <>
            <CloudSlash size={16} weight="duotone" className="text-[var(--vs-warning)] shrink-0" />
            <span className="font-medium">You're offline.</span>
            <span className="text-[var(--vs-text-secondary)]">Showing last-loaded data — new changes will sync when you reconnect.</span>
          </>
        ) : (
          <>
            <ArrowsClockwise size={16} weight="duotone" className="text-[var(--vs-success)] shrink-0" />
            <span className="font-medium">Back online.</span>
            <span className="text-[var(--vs-text-secondary)]">Pull to refresh for the latest data.</span>
          </>
        )}
      </div>
      {offline && (
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="text-xs underline opacity-80 hover:opacity-100"
          data-testid="offline-retry"
        >
          Retry
        </button>
      )}
    </div>
  );
}

export default OfflineBanner;
